import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import api from '../services/api'
import { useAuth } from './AuthContext'
import { ALL_OPCOS_CODE, getWorkspaceCode, isOpcoFlagOn, workspaceNav, workspacePath } from '../config/opcoWorkspace'

// OpCo workspace state for the whole app.
//
// Data comes from GET /api/opco/mine:
//   { enabled: bool, is_opco_admin: bool,
//     opcos: [{ code, name, role: 'member'|'manager', is_primary, opco_id? }] }
// (for OpCo admins `opcos` lists every active OpCo of the org.)
// Any failure (404 before that endpoint exists, network, flag off on the
// backend) means enabled=false: the app then behaves exactly as without OpCos.
//
// The active workspace is the URL prefix /o/<code>/ (see config/opcoWorkspace).
// With the flag on and no prefix, once /mine loads we redirect to the primary
// OpCo (OpCo admins with no membership: All OpCos), keeping the current path.

const DISABLED = {
  enabled: false,
  loaded: true,
  opco: null,
  opcos: [],
  isOpcoAdmin: false,
  isManager: false,
  allOpcos: false,
  workspaceCode: null,
  switchTo: () => {},
}

const OpCoContext = createContext(DISABLED)

function normalizeOpcos(list) {
  if (!Array.isArray(list)) return []
  return list
    .filter((o) => o && typeof o.code === 'string' && o.code)
    .map((o) => ({
      code: o.code,
      name: o.name || o.code,
      role: o.role === 'manager' ? 'manager' : 'member',
      is_primary: Boolean(o.is_primary),
      opco_id: o.opco_id ?? o.id ?? null,
    }))
}

export function defaultWorkspaceCode(opcos, isOpcoAdmin) {
  const primary = opcos.find((o) => o.is_primary) || opcos[0]
  if (primary) return primary.code
  return isOpcoAdmin ? ALL_OPCOS_CODE : null
}

export function OpCoProvider({ children }) {
  const flagOn = isOpcoFlagOn()
  const { isAuthenticated, accessToken } = useAuth()
  const [state, setState] = useState(() => ({
    loaded: !flagOn,
    enabled: false,
    opcos: [],
    isOpcoAdmin: false,
  }))

  useEffect(() => {
    if (!flagOn || !isAuthenticated || !accessToken) return undefined
    let cancelled = false
    let retry = null
    const load = (canRetry) => api.get('/opco/mine')
      .then((res) => {
        if (cancelled) return
        const data = res?.data || {}
        const next = {
          loaded: true,
          enabled: data.enabled !== false,
          opcos: normalizeOpcos(data.opcos),
          isOpcoAdmin: Boolean(data.is_opco_admin),
        }
        // A person's very first requests can overlap with the automatic OpCo
        // assignment; one quiet second look avoids a false "No OpCo access yet".
        if (canRetry && next.enabled && !next.isOpcoAdmin && next.opcos.length === 0) {
          retry = setTimeout(() => load(false), 2000)
          return
        }
        setState(next)
      })
      .catch(() => {
        if (!cancelled) setState({ loaded: true, enabled: false, opcos: [], isOpcoAdmin: false })
      })
    load(true)
    return () => { cancelled = true; if (retry) clearTimeout(retry) }
  }, [flagOn, isAuthenticated, accessToken])

  const workspaceCode = flagOn ? getWorkspaceCode() : null

  // No /o/<code>/ prefix yet: go to the default workspace, keeping the path.
  useEffect(() => {
    if (!state.enabled || !state.loaded || workspaceCode) return
    const target = defaultWorkspaceCode(state.opcos, state.isOpcoAdmin)
    if (!target) return
    const { pathname, search, hash } = window.location
    workspaceNav.replace(workspacePath(target, `${pathname}${search}${hash}`))
  }, [state, workspaceCode])

  const switchTo = useCallback((code) => {
    if (!code) return
    workspaceNav.assign(workspacePath(code))
  }, [])

  const value = useMemo(() => {
    if (!flagOn) return DISABLED
    if (!state.enabled) {
      return { ...DISABLED, loaded: state.loaded }
    }
    const allOpcos = state.isOpcoAdmin && workspaceCode === ALL_OPCOS_CODE
    const wanted = (workspaceCode || '').toLowerCase()
    const member = state.opcos.find((o) => o.code.toLowerCase() === wanted) || null
    const opco = member ? { code: member.code, name: member.name, role: member.role } : null
    return {
      enabled: true,
      loaded: state.loaded,
      opco,
      opcos: state.opcos,
      isOpcoAdmin: state.isOpcoAdmin,
      isManager: state.isOpcoAdmin || opco?.role === 'manager',
      allOpcos,
      workspaceCode,
      switchTo,
    }
  }, [flagOn, state, workspaceCode, switchTo])

  return <OpCoContext.Provider value={value}>{children}</OpCoContext.Provider>
}

export function useOpCo() {
  return useContext(OpCoContext)
}

export default OpCoContext
